import { Volume2, Volume1, VolumeX, Music, Music2 } from "lucide-react";
import { useAudio } from "@/context/GameFeel";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

const base = "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors duration-200";
const on = "bg-purple-500/15 border-purple-500/40 text-purple-200 hover:bg-purple-500/25";
const off = "bg-white/5 border-white/10 text-gray-500 hover:bg-white/10";

function VolumeIcon({ prefs }) {
  if (prefs.muted || prefs.volume === 0) return <VolumeX className="w-4 h-4" />;
  return prefs.volume < 50 ? <Volume1 className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />;
}

// Mute + master volume + per-channel switches. Rendered in the desktop popover and inline in the mobile menu.
export function SoundPanel() {
  const { prefs, unlocked, toggleSound, toggleMusic, toggleMute, previewVolume, setVolume } = useAudio();
  const row = "flex items-center justify-between gap-3 py-1.5";
  return (
    <div data-testid="sound-panel" className="space-y-1 text-sm">
      {!unlocked && <p data-testid="sound-unlock-hint" className="text-[11px] text-gray-500 pb-1">Audio starts after your first tap.</p>}
      <div className={row}>
        <span className="flex items-center gap-2 text-gray-200 font-semibold"><VolumeX className="w-4 h-4 text-gray-400" />Mute all</span>
        <Switch data-testid="mute-switch" checked={prefs.muted} onCheckedChange={toggleMute} className="bg-white/15 data-[state=checked]:bg-red-500 [&>span]:bg-white" />
      </div>
      <div className="py-1.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-200 font-semibold">Volume</span>
          <span data-testid="volume-value" className="font-mono text-xs text-gray-400">{prefs.volume}%</span>
        </div>
        <Slider data-testid="volume-slider" value={[prefs.volume]} min={0} max={100} step={5} disabled={prefs.muted}
          className="[&>span:first-child]:bg-white/15 [&>span:first-child>span]:bg-purple-500 [&_[role=slider]]:bg-white [&_[role=slider]]:border-purple-400 data-[disabled]:opacity-40"
          onValueChange={([v]) => previewVolume(v)} onValueCommit={([v]) => setVolume(v)} aria-label="Volume" />
      </div>
      <div className={row}>
        <span className="flex items-center gap-2 text-gray-300"><Volume2 className="w-4 h-4 text-gray-400" />Sound effects</span>
        <Switch data-testid="sfx-switch" checked={prefs.sound} onCheckedChange={toggleSound} className="bg-white/15 data-[state=checked]:bg-purple-500 [&>span]:bg-white" />
      </div>
      <div className={row}>
        <span className="flex items-center gap-2 text-gray-300"><Music className="w-4 h-4 text-gray-400" />Music</span>
        <Switch data-testid="music-switch" checked={prefs.music} onCheckedChange={toggleMusic} className="bg-white/15 data-[state=checked]:bg-purple-500 [&>span]:bg-white" />
      </div>
    </div>
  );
}

export default function SoundToggles({ inline = false }) {
  const { prefs, toggleMusic } = useAudio();
  if (inline) return <SoundPanel />;
  const live = !prefs.muted && prefs.volume > 0;
  return (
    <div className="flex items-center gap-1.5" data-testid="sound-toggles">
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid="sound-settings-button" aria-label="Sound settings"
            title={prefs.muted ? "Muted — open sound settings" : `Volume ${prefs.volume}% — open sound settings`}
            className={`${base} ${live && prefs.sound ? on : off}`}>
            <VolumeIcon prefs={prefs} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 glass border-purple-500/30 bg-[#12121f]/95">
          <SoundPanel />
        </PopoverContent>
      </Popover>
      <button type="button" data-testid="music-toggle" aria-pressed={prefs.music} onClick={toggleMusic}
        title={prefs.music ? "Music on" : "Music off"} className={`${base} ${prefs.music && live ? on : off}`}>
        {prefs.music ? <Music className="w-4 h-4" /> : <Music2 className="w-4 h-4 opacity-60" />}
      </button>
    </div>
  );
}
